import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { MoreActions } from '../MoreActions/MoreActions';
import type { IMoreActionItem } from '../MoreActions/MoreActions.types';
import api from '../../utils/axios';
import { formatArtifactTitle } from '../../utils/parseArtifacts';
import { filenameFromContentDisposition } from '../../utils/fileUtils';
import { IArtifactCardProps } from './ArtifactCard.type';
import { Permissions } from '../../constants';


export const ArtifactCard: React.FC<IArtifactCardProps> = ({
  title,
  type = 'HTML',
  onClick,
  className = '',
  artifactId,
  identifier,
  messageId,
  content,
  version,
  project,
  threadId,
  isSaved = false,
  isStreaming = false,
  onArtifactSaved,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookingUpArtifact, setLookingUpArtifact] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const hasUpdatePermission = project?.permissions?.includes(Permissions.UpdateProjectPermission) ?? false;
  const hasDownloadPermission = project?.permissions?.includes(Permissions.DownloadPermission) ?? false;

  const { projectId } = useParams();

  const handleCardClick = () => {
    if (onClick && !isStreaming) {
      onClick();
    }
  };

  const handleOpenInNewTab = async (e: React.MouseEvent | undefined) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!projectId) {
      alert('No project selected.');
      return;
    }

    let finalArtifactId: string;

    if (isSaved && artifactId) {
      // For saved artifacts, artifactId is the MongoDB ObjectId (database ID)
      finalArtifactId = artifactId;
    } else {
      // For unsaved artifacts, create or find temporary artifact using identifier
      if (!identifier) {
        alert('Artifact identifier not available.');
        return;
      }
      
      try {
        setLookingUpArtifact(true);
        const contentStr = content || '';
        const size = new Blob([contentStr]).size;
        
        const response = await api.post(`/projects/${projectId}/artifacts/temp-for-view`, {
          artifactId: identifier,
          messageId: messageId,
          title: title,
          content: contentStr,
          type: type || 'text/html',
          version: version, // Include version when creating temp artifact
          size: size // Include size in bytes
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        finalArtifactId = response.data.id || response.data._id;
      } catch (error) {
        console.error('Error creating temporary artifact:', error);
        alert('Failed to create temporary artifact. Please try again.');
        return;
      } finally {
        setLookingUpArtifact(false);
      }
    }

    if (!finalArtifactId) {
      alert('Artifact ID not available.');
      return;
    }

    const url = `/project/${projectId}/artifact/${finalArtifactId}`;
    window.open(url, '_blank');
  };

  const handleDownload = async () => {
    const idToUse = artifactId || identifier; // Use database ID if saved, otherwise identifier
    if (!projectId || !idToUse) return;
    
    setDownloading(true);
    try {
      const contentStr = content || '';
      const size = new Blob([contentStr]).size;
      
      const response = await api.post(`/projects/${projectId}/artifacts/download-with-temp`, {
        artifactId: idToUse,
        messageId: messageId || `artifact_${idToUse}`,
        title,
        content: contentStr,
        type: type || 'text/plain',
        version: version, // Include version when downloading
        size: size // Include size in bytes
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameFromContentDisposition(response.headers['content-disposition'], title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download artifact. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    console.log('Saving artifact', projectId, identifier, messageId, threadId);
    if (!projectId || !identifier) return;
    
    setSaving(true);
    try {
      let url = `/projects/${projectId}/artifacts/`;
      const queryParams = new URLSearchParams(); 
      if (threadId) {
        queryParams.append('thread_id', threadId);
      }
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      // Calculate size in bytes
      const contentStr = content || '';
      const size = new Blob([contentStr]).size;
      
      const response = await api.post(url, {
        identifier: identifier,
        artifactId: identifier,
        title,
        content: contentStr,
        type: type || 'text/plain',
        version: version, // Include version when saving
        messageId: messageId, // Use camelCase to match backend model
        project_id: projectId,
        size: size, // Include size in bytes
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      // Get the database ID from the response
      const databaseId = response.data._id || response.data.id;

      // Trigger global event for requirement folders to reload
      window.dispatchEvent(new Event('artifactSaved'));

      if (onArtifactSaved) {
        onArtifactSaved(databaseId);
      }
    } catch (error) {
      alert('Failed to save artifact. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const menuItems: IMoreActionItem[] = [
    {
      id: 'open in new tab',
      label: "Open in new tab",
      onClick: () => handleOpenInNewTab(undefined),
    },
    ...(hasDownloadPermission ? [{
      id: 'download',
      label: downloading ? 'Downloading...' : 'Download',
      onClick: handleDownload,
      disabled: downloading as boolean,
    } as IMoreActionItem] : []),
   ...(hasUpdatePermission ? [{
        id: 'separator',
        label: '',
        type: 'separator' as const,
      },
      {
          id: 'save',
          label: saving ? 'Saving...' : 'Save',
          onClick: handleSave,
          disabled: saving as boolean,
        },
      ]
    : [])
  ];

  return (
    <div
      className={`
        bg-bg-brand-primary-alt
        border border-utility-brand-100-alt
        rounded-bg-v2-md
        px-bg-v2-lg
        py-bg-v2-md
        flex
        items-center
        gap-bg-v2-lg
        ${isStreaming ? 'cursor-default' : 'cursor-pointer'}
        ${isStreaming ? '' : 'hover:shadow-bg-v2-sm'}
        transition-shadow
        ${isStreaming ? 'relative overflow-hidden' : ''}
        ${className}
      `}
      onClick={handleCardClick}
    >
      {/* Left side: Icon and Content */}
      <div className={`flex items-center gap-bg-v2-lg flex-1 min-w-0 ${isStreaming ? 'skeleton-pulse' : ''}`}>
        {/* Code icon on a solid dark tile — inverts with the theme so the glyph
            keeps full contrast against it either way. */}
        <div className="relative flex-shrink-0">
          <div className="w-[34px] h-[34px] rounded-bg-v2-md bg-fg-primary flex items-center justify-center">
            <Icon 
              name={IconName.Code} 
              size="sm" 
              className="text-bg-primary"
              inheritColor
            />
          </div>
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-bg-v2-xxs flex-1 min-w-0">
          {/* Title */}
          <Text 
            variant="text-sm"
            weight="medium" 
            color="primary"
            className="truncate"
            as={'span'}
          >
            {formatArtifactTitle(title, version)}
          </Text>

          {/* Type and Status */}
          <div className="flex items-center">
            <Text 
              variant="text-xs" 
              weight="medium" 
              color="tertiary"
              as={'span'}
            >
              {type}
            </Text>
          </div>
        </div>
      </div>

      {/* Right side: Action buttons */}
      <div className="flex items-center gap-bg-v2-xs flex-shrink-0">
        {/* Open in new tab button - hidden when streaming */}
        {!isStreaming && (
          <button
            onClick={handleOpenInNewTab}
            disabled={lookingUpArtifact}
            className="
              w-bg-v2-4xl
              h-bg-v2-4xl
              rounded-full
              flex
              items-center
              justify-center
              hover:bg-utility-brand-100-alt
              transition-colors
              disabled:opacity-50
              disabled:cursor-not-allowed
            "
            aria-label="Open in new tab"
          >
            {lookingUpArtifact ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-utility-brand-600"></div>
            ) : (
              <Icon
                name={IconName.ArrowNarrowUpRight}
                size="md"
                className="text-fg-quaternary"
                inheritColor
              />
            )}
          </button>
        )}

        {/* More menu button - hidden when streaming */}
        {!isStreaming && (
          <div className="relative">
            <button
              ref={menuRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="
                w-bg-v2-4xl
                h-bg-v2-4xl
                rounded-full
                flex
                items-center
                justify-center
                hover:bg-utility-brand-100-alt
                transition-colors
              "
              aria-label="More options"
            >
              <Icon
                name={IconName.More}
                size="sm"
                variant={"secondary"}
              />
            </button>

            <MoreActions
              items={menuItems}
              isOpen={menuOpen}
              onClose={() => setMenuOpen(false)}
              position="bottom-left"
              triggerRef={menuRef}
            />
          </div>
        )}
      </div>
    </div>
  );
};

